import React, { useCallback } from 'react'
import cx from 'classnames'
import { useAppDispatch } from 'reduxTyped'
import { Icon, l, sanitizeURL } from '@manychat/manyui'

import * as builderStateActions from 'common/builder/actions/builderStateActions'
import { IBlockStats } from 'common/builder/blocks/blockInterfaces'
import { IButtonStats } from 'common/builder/buttons/buttonInterfaces'
import FlowTextViewer from 'common/builder/components/misc/FlowTextViewer'
import { convertToVariable } from 'common/builder/constants/PhoneInputType'
import { getButtonUrl } from 'common/builder/utils/getButtonUrl'
import { getCtr } from 'common/builder/utils/getCtr'
import { Col, Row } from 'components/Grid'
import errorTrackingService from 'utils/services/errorTrackingService'

import cm from './StatsViewer.module.css'

interface StatsViewerProps {
  builderId: string
  phone?: string | null
  url?: string | null
  stats: IBlockStats | IButtonStats | null
  isSimple?: boolean
  deliveredUniqueUsers?: number | null
}

export const StatsViewer = ({
  builderId,
  phone,
  stats,
  url,
  isSimple,
  deliveredUniqueUsers,
}: StatsViewerProps) => {
  const dispatch = useAppDispatch()
  const segmentId = stats?.segmentId
  const clicksCount = stats?.clicks || 0
  const usersCount = stats?.users || 0
  const canSelectSegment = usersCount > 0
  const isCtrEnabled = !(stats ? stats.ctrDisabled : true)

  const selectSegment = useCallback(() => {
    if (!segmentId) {
      errorTrackingService.trackWarning('Unable to select select segment without segmentId')
      return
    }

    dispatch(builderStateActions.selectSegment(builderId, segmentId))
  }, [builderId, dispatch, segmentId])

  const hideClickedStats = isSimple && url

  return (
    <div className={cm.stats}>
      {phone && (
        <div>
          {l.translate('Phone number:')}{' '}
          <strong>
            <FlowTextViewer value={convertToVariable(phone)} />
          </strong>
        </div>
      )}

      {url && (
        <Row>
          <Col width={40}>URL:</Col>

          <Col>
            <a
              className="text-ellipsis"
              href={sanitizeURL(getButtonUrl(url))}
              data-title={l.translate('Open in new window')}
              data-title-at="bottom"
              target="_blank"
              rel="noreferrer"
            >
              <strong>
                <FlowTextViewer value={url} preview={false} />
              </strong>
            </a>
          </Col>
        </Row>
      )}

      {isCtrEnabled && (
        <div>
          {l.translate('Total clicks:')} <strong>{clicksCount}</strong>
        </div>
      )}

      {!hideClickedStats && (
        <div
          className={cx({ [cm.link]: canSelectSegment })}
          onClick={canSelectSegment ? selectSegment : undefined}
        >
          {l.translate('Users clicked:')} <strong>{usersCount}</strong>
        </div>
      )}

      {isSimple && (
        <div>
          CTR{' '}
          <Icon.Question
            size={14}
            data-title={l.translate(
              'The metric is calculated as clicks on the button of the ad message divided by clicks on ad displayed to a contact by Facebook and expressed in percents. Mind that testing your own click-to-messenger ad using Preview feature available in Facebook Ads Manager affects clicks statistics!',
            )}
          />
          :{' '}
          <strong>
            {getCtr({
              usersTotal: usersCount,
              deliveredUniqueUsers: deliveredUniqueUsers || 0,
            })}
          </strong>
        </div>
      )}
    </div>
  )
}
